/**
 * @file useSubscription hook — plan status, upgrade flow, referral info.
 *
 * Provides:
 *   - Current plan tier (GRATIS/TRIAL/PRO)
 *   - Effective tier based on plan + trial state
 *   - Appointment usage (current/limit)
 *   - Referral stats and code
 *   - Trial activation, upgrade via Stripe Checkout
 *   - Manage subscription via Stripe Portal
 *
 * @module hooks/useSubscription
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { subscriptionService } from "../services";
import { SUBSCRIPTION_STRINGS as S } from "../constants/strings";

/**
 * Hook for subscription management.
 * @param {Object} [options]
 * @param {boolean} [options.autoFetch=true] - Fetch plan status on mount.
 * @returns {Object} Subscription state and actions.
 */
export function useSubscription(options = {}) {
  const { autoFetch = true } = options;
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);

  const fetchStatus = useCallback(async (retries = 1) => {
    try {
      setIsLoading(true);
      const response = await subscriptionService.getStatus();
      setStatus(response.data);
    } catch (err) {
      console.error("Error fetching subscription status:", err);
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, 2000));
        return fetchStatus(retries - 1);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await subscriptionService.getReferrals();
      setReferrals(response.data || []);
    } catch (err) {
      console.error("Error fetching referrals:", err);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) fetchStatus();
  }, [autoFetch, fetchStatus]);

  /** Redirect to Stripe Checkout for Pro upgrade. */
  const startCheckout = async (tier) => {
    try {
      const response = await subscriptionService.createCheckout(tier ? { tier } : {});
      const { url } = response.data;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || S.UPGRADE_ERROR);
    }
  };

  /** Activate the one-time 15-appointment Pro trial. Returns true on success. */
  const activateTrial = async () => {
    try {
      await subscriptionService.activateTrial();
      await fetchStatus();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error al activar prueba");
      return false;
    }
  };

  /** Redirect to Stripe Customer Portal to manage subscription. */
  const openPortal = async () => {
    try {
      const response = await subscriptionService.createPortal();
      const { url } = response.data;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || S.PORTAL_ERROR);
    }
  };

  const effectiveTier = status?.effectiveTier || "GRATIS";
  const proTrialRemaining = status?.proTrialRemaining;

  return {
    status,
    isLoading,
    referrals,
    effectiveTier,
    proTrialRemaining,
    isPro: status?.plan === "PRO",
    isFree: status?.plan === "GRATIS",
    isOnTrial: effectiveTier === "TRIAL",
    canActivateTrial: proTrialRemaining === null && status?.plan === "GRATIS",
    trialExpired: proTrialRemaining === 0,
    fetchStatus,
    fetchReferrals,
    activateTrial,
    startCheckout,
    openPortal,
  };
}
